import { FieldType, IEventMetadata, IFormFieldMetadata, IFormFieldValueMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, IInstitution, IPatient, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormField, ISourceDataConfigurationFormFieldValue, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, ISourceDataEvent, ISourceDataField, ISourceDataFieldAudit, ISourceDataFinding, ISourceDataForm, ITrial, ITrialFormMetadata } from "@/api/dtos";
import { FC, RefObject } from "react";
import { IHasChildren } from '@ngt/forms';
import { VirtuosoHandle } from "react-virtuoso";
import { DateTime } from "luxon";


export enum SourceDataType {
    Event = 1,
    Form = 2,
    Section = 3,
    Field = 4
}

export interface ISourceDataVerificationEvent {
    type: SourceDataType.Event;
    configuration?: ISourceDataConfiguration;
    trialConfiguration?: ISourceDataConfiguration;
    metadata?: ITrialFormMetadata;
    sourceData?: ISourceData;
    eventKey?: string;
    eventRepeat?: number;
    eventIdentifier?: string;
    eventPropertyPath?: string;
    eventEnabled?: boolean;
    eventConfiguration?: ISourceDataConfigurationEvent;
    eventLocalConfiguration?: ISourceDataConfigurationEvent;
    eventTrialConfiguration?: ISourceDataConfigurationEvent;
    eventMetadata?: IEventMetadata;
    eventSourceData?: ISourceDataEvent;

    usingEventGlobalConfiguration: boolean;
    usingEventTrialConfiguration: boolean;

    eventLabel: string;

    formKeys: string[]

    forms?: ISourceDataVerificationForm[];
}

export interface ISourceDataVerificationForm extends Omit<ISourceDataVerificationEvent, 'formKeys' | 'forms' | 'type'> {
    type: SourceDataType.Form;

    formKey?: string;
    formIdentifier?: string;
    formPropertyPath?: string;
    formEnabled?: boolean;
    formConfiguration?: ISourceDataConfigurationForm;
    formLocalConfiguration?: ISourceDataConfigurationForm;
    formGlobalConfiguration?: ISourceDataConfigurationForm;
    formTrialConfiguration?: ISourceDataConfigurationForm;
    formMetadata?: IFormMetadata;
    formLabel: string;

    usingFormGlobalConfiguration: boolean;
    usingFormTrialConfiguration: boolean;

    formVersionKey?: string;
    formVersionEnabled?: boolean;
    formVersionConfiguration?: ISourceDataConfigurationFormVersion;
    formVersionLocalConfiguration?: ISourceDataConfigurationFormVersion;
    formVersionGlobalConfiguration?: ISourceDataConfigurationFormVersion;
    formVersionTrialConfiguration?: ISourceDataConfigurationFormVersion;
    formVersionMetadata?: IFormVersionMetadata;
    formVersionLabel: string;
    formSourceData?: ISourceDataForm;

    usingFormVersionGlobalConfiguration: boolean;
    usingFormVersionTrialConfiguration: boolean;

    sections: Record<string, boolean>;

    fieldKeys: string[];

    fields?: (ISourceDataVerificationSection | ISourceDataVerificationField)[];
}

export interface ISourceDataVerificationSection extends Omit<ISourceDataVerificationForm, 'sections' | 'fieldKeys' | 'fields' | 'type'> {
    type: SourceDataType.Section;

    formSourceData?: ISourceDataForm;
    formSectionKey?: string;
    formSectionRepeat?: number;
    formSectionIdentifier?: string;
    formSectionEnabled?: boolean;
    formSectionConfiguration?: ISourceDataConfigurationFormSection;
    formSectionLocalConfiguration?: ISourceDataConfigurationFormSection;
    formSectionGlobalConfiguration?: ISourceDataConfigurationFormSection;
    formSectionTrialConfiguration?: ISourceDataConfigurationFormSection;
    formSectionMetadata?: IFormSectionMetadata;
    formSectionLabel?: string;

    usingFormSectionGlobalConfiguration?: boolean;
    usingFormSectionTrialConfiguration?: boolean;

    fieldKeys: string[]
    fields?: ISourceDataVerificationField[];
}

export interface ISourceDataVerificationField extends Omit<ISourceDataVerificationSection, 'fieldKeys' | 'fields' | 'type'> {
    type: SourceDataType.Field;
    
    formFieldKey?: string;
    formFieldIdentifier?: string;
    formFieldPropertyPath?: string;
    formFieldEnabled?: boolean;
    formFieldConfiguration?: ISourceDataConfigurationFormField;
    formFieldLocalConfiguration?: ISourceDataConfigurationFormField;
    formFieldGlobalConfiguration?: ISourceDataConfigurationFormField;
    formFieldTrialConfiguration?: ISourceDataConfigurationFormField;
    formFieldMetadata?: IFormFieldMetadata;
    formFieldSourceData?: ISourceDataField;

    usingFormFieldGlobalConfiguration: boolean;
    usingFormFieldTrialConfiguration: boolean;

    formFieldLabel: string;

    formFieldValueKey?: string;
    formFieldValueConfiguration?: ISourceDataConfigurationFormFieldValue;
    formFieldValueLocalConfiguration?: ISourceDataConfigurationFormFieldValue;
    formFieldValueGlobalConfiguration?: ISourceDataConfigurationFormFieldValue;
    formFieldValueTrialConfiguration?: ISourceDataConfigurationFormFieldValue;
    formFieldValueMetadata?: IFormFieldValueMetadata;
    formFieldValue?: string;
    formFieldTypeLabel?: string;

    usingFormFieldValueGlobalConfiguration: boolean;
    usingFormFieldValueTrialConfiguration: boolean;

    audits?: ISourceDataVerificationFieldAudit[]
}

export interface ISourceDataVerificationFieldAudit extends ISourceDataFieldAudit {
    oldValueConverted?: string; 
    newValueConverted?: string;
}

export type ISourceDataVerificationItem = ISourceDataVerificationEvent | ISourceDataVerificationForm | ISourceDataVerificationSection | ISourceDataVerificationField;

export interface ISourceDataFormLayoutComponentProps extends IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hasBreadcrumbs?: boolean;
    hideVerification?: boolean;
    sourceData?: ISourceData;
}

export interface ISourceDataFormEventComponentProps extends ISourceDataVerificationEvent, IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hideVerification?: boolean;
}

export interface ISourceDataFormFormComponentProps extends ISourceDataVerificationForm, IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hideVerification?: boolean;
}

export interface ISourceDataFormCollectionComponentProps extends ISourceDataVerificationSection, IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hideVerification?: boolean;
}

export interface ISourceDataFormGroupComponentProps extends ISourceDataVerificationSection, IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hideVerification?: boolean;
}

export interface ISourceDataFormFieldComponentProps extends ISourceDataVerificationField, IHasChildren {
    settings?: ISourceDataFormSettings;
    Layout: ISourceDataFormLayoutComponents;
    events: ISourceDataVerificationEvent[];
    list?: ISourceDataVerificationItem[];
    trial?: ITrial;
    institution?: IInstitution;
    patient?: IPatient;
    readOnly?: boolean;
    hideVerification?: boolean;
}

export interface ISourceDataFormLayoutComponents {
    Layout: FC<ISourceDataFormLayoutComponentProps>,
    Event: FC<ISourceDataFormEventComponentProps>,
    Form: FC<ISourceDataFormFormComponentProps>,
    Collection: FC<ISourceDataFormCollectionComponentProps>,
    Group: FC<ISourceDataFormGroupComponentProps>,
    Field: FC<ISourceDataFormFieldComponentProps>
}

export interface ISourceDataFormSettings {
    layoutType?: 'Expanding' | 'Titled';
    hideEvents?: boolean;
    hideForms?: boolean;
    hideVersions?: boolean;
    autoEvents?: boolean;
    autoForms?: boolean;
    autoGroups?: boolean;
    autoCollections?: boolean;
    showNotes?: boolean;
    showAudit?: boolean;
    showSideNavigation?: boolean;
    showSideNavigationForms?: boolean;
    showSideNavigationReviewProgress?: boolean;
    showSideNavigationVerificationProgress?: boolean;
}

class SourceDataFormProcessor {
    public processAllEvents = async (configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, data?: ISourceData, metadata?: ITrialFormMetadata) => {
        const eventKeys = configuration?.events ? Object.keys(configuration.events) : [];

        const events: ISourceDataVerificationEvent[] = [];

        for (let eventKey of eventKeys) {
           await this.processEvent(eventKey, events, configuration, trialConfiguration, data, metadata);
        }

        return events;
    }
    
    private processAllForms = async (event: ISourceDataVerificationEvent) => {
        const formKeys = event.formKeys ?? [];

        const forms: ISourceDataVerificationForm[] = [];

        for (let formKey of formKeys) {
            await this.processForm(formKey, forms, event);
        }

        return forms;
    }
    
    private processAllFormFields = async (form: ISourceDataVerificationForm) => {
        const fieldKeys = form.fieldKeys ?? [];

        const fields: (ISourceDataVerificationSection|ISourceDataVerificationField)[] = [];

        for (let fieldKey of fieldKeys) {
            await this.processFormFields(fieldKey, fields, form);
        }

        return fields;
    }
    
    private processAllSectionFields = async (section: ISourceDataVerificationSection) => {
        const fieldKeys = section.fieldKeys ?? [];

        const fields: ISourceDataVerificationField[] = [];

        for (let fieldKey of fieldKeys) {
            await this.processSectionFields(fieldKey, fields, section);
        }

        return fields;
    }
    
    private processEvent = async (eventKey: string, events: ISourceDataVerificationEvent[], configuration?: ISourceDataConfiguration, trialConfiguration?: ISourceDataConfiguration, sourceData?: ISourceData, metadata?: ITrialFormMetadata ) => {
        const eventLocalConfiguration = configuration!.events![eventKey];
        const eventTrialConfiguration = trialConfiguration?.events?.[eventKey];
        const eventMetadata = metadata?.events?.find(x => x.id === eventKey);
        
        let eventConfiguration: ISourceDataConfigurationEvent | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!eventLocalConfiguration && !eventLocalConfiguration.linked) {
            eventConfiguration = eventLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else {
            eventConfiguration = eventTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const baseEvent: ISourceDataVerificationEvent = {
            type: SourceDataType.Event,
            configuration: configuration,
            metadata: metadata,
            sourceData: sourceData,
            trialConfiguration: trialConfiguration,
            eventKey: eventKey,
            eventEnabled: eventLocalConfiguration?.enabled,
            eventConfiguration: eventConfiguration,
            eventLocalConfiguration: eventLocalConfiguration,
            eventTrialConfiguration: eventTrialConfiguration,
            eventMetadata: eventMetadata,
            eventSourceData: undefined,
            eventLabel: eventConfiguration?.name ?? eventMetadata?.name ?? 'Unknown Event',
            usingEventTrialConfiguration: usingTrialConfiguration,
            usingEventGlobalConfiguration: usingGlobalConfiguration,
            forms: undefined,
            formKeys: eventConfiguration?.forms ? Object.keys(eventConfiguration.forms) : [],
        }

        const eventSourceData = sourceData?.events?.[eventKey];

        const minimumRepeat = baseEvent.eventMetadata?.repeating ? 
            baseEvent.eventConfiguration?.minimumRepeat ?? 1 : 
            1;

        const limitedSourceData: Record<number, ISourceDataEvent | undefined> = {...eventSourceData};

        for (let repeat = 1; repeat <= minimumRepeat; repeat++) {
            if (!limitedSourceData[repeat]) {
                limitedSourceData[repeat] = undefined;
            }
        }

        const repeats = Object.keys(limitedSourceData);

        for (let repeat of repeats) {
            const event: ISourceDataVerificationEvent = {
                ...baseEvent,
                forms: undefined,
                eventRepeat: +repeat,
                eventSourceData: limitedSourceData[+repeat],
                eventIdentifier: `${baseEvent.eventKey}-${repeat}`,
                eventPropertyPath: `events.${baseEvent.eventKey}.${repeat}`
            }

            event.forms = await this.processAllForms(event);

            events.push(event);
        }

        if (repeats.length === 0) {
            const event: ISourceDataVerificationEvent = {
                ...baseEvent,
                eventIdentifier: baseEvent.eventKey
            }

            events.push(event);
        }
    }

    private processForm = async (formKey: string, forms: ISourceDataVerificationForm[], event: ISourceDataVerificationEvent) => {
        const formLocalConfiguration = event.eventConfiguration?.forms?.[formKey];
        const formGlobalConfiguration = event.configuration?.forms?.[formKey];
        const formTrialConfiguration = event.trialConfiguration?.forms?.[formKey];

        let formConfiguration: ISourceDataConfigurationForm | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!formLocalConfiguration && !formLocalConfiguration?.linked) {
            formConfiguration = formLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else if (!!formGlobalConfiguration && !formGlobalConfiguration.linked) {
            formConfiguration = formGlobalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = true;
        }
        else {
            formConfiguration = formTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const formMetadata = event.metadata?.forms?.find(x => x.id == formKey);
        const formSourceData = event.eventSourceData?.forms?.[formKey];
        const formVersionKey = formSourceData?.formVersionMetadataId;
        const formVersionLocalConfiguration = formVersionKey ? formLocalConfiguration?.versions?.[formVersionKey] : undefined;
        const formVersionGlobalConfiguration = formVersionKey ? formGlobalConfiguration?.versions?.[formVersionKey] : undefined;
        const formVersionTrialConfiguration = formVersionKey ? formTrialConfiguration?.versions?.[formVersionKey] : undefined;

                
        let formVersionConfiguration: ISourceDataConfigurationFormVersion | undefined = undefined;
        let usingFormVersionTrialConfiguration: boolean = false;
        let usingFormVersionGlobalConfiguration: boolean = false;

        if (!!formVersionLocalConfiguration && !formVersionLocalConfiguration?.linked) {
            formVersionConfiguration = formVersionLocalConfiguration;
            usingFormVersionTrialConfiguration = false;
            usingFormVersionGlobalConfiguration = false;
        }
        else if (!!formVersionGlobalConfiguration && !formVersionGlobalConfiguration.linked) {
            formVersionConfiguration = formVersionGlobalConfiguration;
            usingFormVersionTrialConfiguration = false;
            usingFormVersionGlobalConfiguration = true;
        }
        else {
            formVersionConfiguration = formVersionTrialConfiguration;
            usingFormVersionTrialConfiguration = true;
            usingFormVersionGlobalConfiguration = false;
        }
        
        const formVersionMetadata = formVersionKey ? formMetadata?.versions?.find(x => x.id === formVersionKey) : undefined;

        const form: ISourceDataVerificationForm = {
            ...event,
            type: SourceDataType.Form,
            formKey: formKey,
            formIdentifier: `${event.eventIdentifier}-${formKey}`,
            formPropertyPath: `${event.eventPropertyPath}.forms.${formKey}`,
            formEnabled: formLocalConfiguration?.enabled,
            formConfiguration: formConfiguration,
            formLocalConfiguration: formLocalConfiguration,
            formGlobalConfiguration: formGlobalConfiguration,
            formTrialConfiguration: formTrialConfiguration,
            formMetadata: formMetadata,
            usingFormGlobalConfiguration: usingGlobalConfiguration,
            usingFormTrialConfiguration: usingTrialConfiguration,
            formLabel: formConfiguration?.name ?? formMetadata?.name ?? 'Unknown Form',
            formVersionKey: formVersionKey,
            formVersionEnabled: !!formLocalConfiguration && !formLocalConfiguration?.linked ? formVersionLocalConfiguration?.enabled : formVersionGlobalConfiguration?.enabled,
            formVersionConfiguration: formVersionConfiguration,
            formVersionLocalConfiguration: formVersionLocalConfiguration,
            formVersionGlobalConfiguration: formVersionGlobalConfiguration,
            formVersionTrialConfiguration: formVersionTrialConfiguration,
            formVersionMetadata: formVersionMetadata,
            formVersionLabel: formVersionConfiguration?.name ?? formVersionMetadata?.name ?? (formSourceData?.formVersionMetadataId ? 'Unknown Version' : ''),
            usingFormVersionGlobalConfiguration: usingFormVersionGlobalConfiguration,
            usingFormVersionTrialConfiguration: usingFormVersionTrialConfiguration,
            formSourceData: formSourceData,
            sections: {},
            fields: undefined,
            fieldKeys: formVersionMetadata?.fields?.map(field => field.id!) ?? []
        }

        form.fields = await this.processAllFormFields(form);

        forms.push(form);
    }

    private processFormFields = async (fieldKey: string, fields: (ISourceDataVerificationSection|ISourceDataVerificationField)[], form: ISourceDataVerificationForm) => {
        const fieldMetadata = form.formVersionMetadata?.fields?.find(x => x.id === fieldKey);

        if (fieldMetadata?.formSectionMetadataId) {
            await this.processFormSection(form, fields, fieldKey, fieldMetadata);
        }
        else {
            await this.processFormField(form, fields, fieldKey, fieldMetadata);
        }

        return true;
    }

    private processSectionFields = async (fieldKey: string,  fields: ISourceDataVerificationField[], section: ISourceDataVerificationSection) => {
        const fieldMetadata = section.formVersionMetadata?.fields?.find(x => x.id === fieldKey);

        await this.processSectionField(section, fields, fieldKey, fieldMetadata);
    }

    private processFormSection = async (form: ISourceDataVerificationForm, fields: (ISourceDataVerificationSection|ISourceDataVerificationField)[], fieldKey: string, fieldMetadata: IFormFieldMetadata) => {
        
        const formSectionKey = fieldMetadata.formSectionMetadataId!;

        if (form.sections[formSectionKey]) {
            return;
        }

        form.sections[formSectionKey] = true;

        const formSectionLocalConfiguration = form.formVersionLocalConfiguration?.sections?.[formSectionKey];
        const formSectionGlobalConfiguration = form.formVersionGlobalConfiguration?.sections?.[formSectionKey];
        const formSectionTrialConfiguration = form.formVersionTrialConfiguration?.sections?.[formSectionKey];

                
        let formSectionConfiguration: ISourceDataConfigurationFormSection | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!formSectionLocalConfiguration && !formSectionLocalConfiguration?.linked) {
            formSectionConfiguration = formSectionLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else if (!!formSectionGlobalConfiguration && !formSectionGlobalConfiguration.linked) {
            formSectionConfiguration = formSectionGlobalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = true;
        }
        else {
            formSectionConfiguration = formSectionTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const formSectionMetadata = form.formVersionMetadata?.sections?.find(x => x.id === formSectionKey);

        const baseSection: ISourceDataVerificationSection = {
            ...form,
            type: SourceDataType.Section,
            formSectionKey: formSectionKey,
            formSectionEnabled: !!formSectionLocalConfiguration && !formSectionLocalConfiguration?.linked ? formSectionLocalConfiguration?.enabled : formSectionGlobalConfiguration?.enabled,
            formSectionConfiguration: formSectionConfiguration,
            formSectionLocalConfiguration: formSectionLocalConfiguration,
            formSectionGlobalConfiguration: formSectionGlobalConfiguration,
            formSectionTrialConfiguration: formSectionTrialConfiguration,
            formSectionMetadata: formSectionMetadata,
            formSectionRepeat: undefined,
            usingFormSectionGlobalConfiguration: usingGlobalConfiguration,
            usingFormSectionTrialConfiguration: usingTrialConfiguration,
            fields: undefined,
            fieldKeys: form.formVersionMetadata?.fields?.filter(x => x.formSectionMetadataId === formSectionKey).map(field => field.id!) ?? [],
            formSectionLabel: formSectionConfiguration?.name ?? formSectionMetadata?.name ?? (formSectionMetadata?.repeating ? 'Unknown Collection' : 'Unknown Group')
        }

        

        const maxRecordedRepeat = form.formSourceData?.fields ?
            Math.max(...Object
                .values(form.formSourceData.fields)
                .map(x => {
                    const repeatNumbers = Object
                        .values(x)
                        .filter(x => x.formSectionMetadataId === formSectionKey)
                        .map(x => x.repeat ?? 1)

                    if (repeatNumbers && repeatNumbers.length > 0) {
                        return Math.max(...repeatNumbers)
                    }

                    return 0;
                })) :
            0;

        const minimumConfigRepeat = baseSection.formSectionMetadata?.repeating ? 
            baseSection.formSectionConfiguration?.minimumRepeat ?? 0: 
            1;

        const minimumRepeat = maxRecordedRepeat > minimumConfigRepeat ? maxRecordedRepeat : minimumConfigRepeat;

        const repeats = minimumRepeat === 0 ? [] : Array.from({length: minimumRepeat}, (_, i) => 1 + i);

        for (let repeat of repeats) {
            const section: ISourceDataVerificationSection = {
                ...baseSection,
                fields: undefined,
                formSectionRepeat: repeat,
                formSectionIdentifier: `${baseSection.formIdentifier}-${baseSection.formSectionKey}-${repeat}`
            }


            section.fields = await this.processAllSectionFields(section);

            fields.push(section);
        }

        if (repeats.length === 0) {
            const section: ISourceDataVerificationSection = {
                ...baseSection,
                formSectionIdentifier: `${baseSection.formIdentifier}-${baseSection.formSectionKey}`,
            }

            fields.push(section);
        }
    }
    
    
    
    private processFormField = async (form: ISourceDataVerificationForm, fields: (ISourceDataVerificationSection|ISourceDataVerificationField)[], fieldKey: string, fieldMetadata?: IFormFieldMetadata) => {
        if (!!fieldMetadata?.parentFormFieldMetadataId) {
            return;
        }
        
        const formFieldLocalConfiguration = form.formVersionLocalConfiguration?.fields?.[fieldKey];
        const formFieldGlobalConfiguration = form.formVersionGlobalConfiguration?.fields?.[fieldKey];
        const formFieldTrialConfiguration = form.formVersionTrialConfiguration?.fields?.[fieldKey];
                
                
        let formFieldConfiguration: ISourceDataConfigurationFormField | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!formFieldLocalConfiguration && !formFieldLocalConfiguration?.linked) {
            formFieldConfiguration = formFieldLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else if (!!formFieldGlobalConfiguration && !formFieldGlobalConfiguration.linked) {
            formFieldConfiguration = formFieldGlobalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = true;
        }
        else {
            formFieldConfiguration = formFieldTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }


        const field: ISourceDataVerificationField = {
            ...form,
            type: SourceDataType.Field,
            formFieldKey: fieldKey,
            formFieldIdentifier: `${form.formIdentifier}-${fieldKey}`,
            formFieldPropertyPath: `${form.formPropertyPath}.fields.${fieldKey}.1`,
            formFieldEnabled: !!formFieldLocalConfiguration && !formFieldLocalConfiguration?.linked ? formFieldLocalConfiguration?.enabled : formFieldGlobalConfiguration?.enabled,
            formFieldConfiguration: formFieldConfiguration,
            formFieldLocalConfiguration: formFieldLocalConfiguration,
            formFieldGlobalConfiguration: formFieldGlobalConfiguration,
            formFieldTrialConfiguration: formFieldTrialConfiguration,
            formFieldMetadata: fieldMetadata,
            formFieldSourceData: form.formSourceData?.fields?.[fieldKey]?.[1],
            formFieldLabel: formFieldConfiguration?.name ?? fieldMetadata?.name ?? 'Unknown Field',
            usingFormFieldGlobalConfiguration: usingGlobalConfiguration,
            usingFormFieldTrialConfiguration: usingTrialConfiguration,
            usingFormFieldValueGlobalConfiguration: false,
            usingFormFieldValueTrialConfiguration: false,
        }
        
        await this.processFormValue(field);
        
        fields.push(field);
    }
    
    private processSectionField = async (section: ISourceDataVerificationSection, fields: ISourceDataVerificationField[], fieldKey: string, fieldMetadata?: IFormFieldMetadata) => {
        if (!!fieldMetadata?.parentFormFieldMetadataId) {
            return;
        }
        
        const formFieldLocalConfiguration = section.formVersionLocalConfiguration?.sections?.[section.formSectionKey!].fields?.[fieldKey];
        const formFieldGlobalConfiguration = section.formVersionGlobalConfiguration?.sections?.[section.formSectionKey!].fields?.[fieldKey];
        const formFieldTrialConfiguration = section.formVersionTrialConfiguration?.sections?.[section.formSectionKey!].fields?.[fieldKey];
       
        let formFieldConfiguration: ISourceDataConfigurationFormField | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!formFieldLocalConfiguration && !formFieldLocalConfiguration?.linked) {
            formFieldConfiguration = formFieldLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else if (!!formFieldGlobalConfiguration && !formFieldGlobalConfiguration.linked) {
            formFieldConfiguration = formFieldGlobalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = true;
        }
        else {
            formFieldConfiguration = formFieldTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const field: ISourceDataVerificationField = {
            ...section,
            type: SourceDataType.Field,
            formFieldKey: fieldKey,
            formFieldIdentifier: `${section.formSectionIdentifier}-${fieldKey}`,
            formFieldPropertyPath: `${section.formPropertyPath}.fields.${fieldKey}.${section.formSectionRepeat}`,
            formFieldEnabled: !!formFieldLocalConfiguration && !formFieldLocalConfiguration?.linked ? formFieldLocalConfiguration?.enabled : formFieldGlobalConfiguration?.enabled,
            formFieldConfiguration: formFieldConfiguration,
            formFieldLocalConfiguration: formFieldLocalConfiguration,
            formFieldGlobalConfiguration: formFieldGlobalConfiguration,
            formFieldTrialConfiguration: formFieldTrialConfiguration,
            formFieldMetadata: fieldMetadata,
            formFieldSourceData: section.formSectionRepeat ? section.formSourceData?.fields?.[fieldKey]?.[section.formSectionRepeat] : undefined,
            formFieldLabel: formFieldConfiguration?.name ?? fieldMetadata?.name ?? 'Unknown Field',
            usingFormFieldGlobalConfiguration: usingGlobalConfiguration,
            usingFormFieldTrialConfiguration: usingTrialConfiguration,
            usingFormFieldValueGlobalConfiguration: false,
            usingFormFieldValueTrialConfiguration: false
        }
        
        await this.processFormValue(field);


        fields.push(field);
    }

    private processFieldValue = async (field: ISourceDataVerificationField, metadata?: IFormFieldValueMetadata, value?: string) => {
        let calculatedValue = field.formFieldValueConfiguration?.name ?? metadata?.name ?? value;
        
        switch (field.formFieldMetadata?.fieldType) {
            case FieldType.Date:
                calculatedValue = value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : undefined;
                break;
            case FieldType.Checkbox:
                calculatedValue = value && value == '1' ? 'Yes' : value && value == '0' ? 'No' : undefined;
                break;
            case FieldType.Ctcae:
                //console.log(field.formFieldMetadata.data)
                break;
        }

        return calculatedValue;
    }

    private processFieldAudit = async (field: ISourceDataVerificationField) => {
        field.audits = [];

        if (field.formFieldSourceData?.audit) {
            for (let audit of field.formFieldSourceData.audit) {
    
                const newMetadataValue = field.formFieldMetadata?.values?.find(x => x.id === audit.newValue);
                const oldMetadataValue = field.formFieldMetadata?.values?.find(x => x.id === audit.oldValue);
                

                field.audits.push({
                    ...audit, 
                    newValueConverted: await this.processFieldValue(field, newMetadataValue, audit.newValue), 
                    oldValueConverted: await this.processFieldValue(field, oldMetadataValue, audit.oldValue)
                })
            }
        }
    }

    private processFormValue = async (field: ISourceDataVerificationField) => {

        switch (field.formFieldMetadata?.fieldType) {
            case FieldType.Checkbox:
                field.formFieldTypeLabel = 'Checkbox';
                break;
            case FieldType.Ctcae:
                field.formFieldTypeLabel = 'CTCAE';
                break;
            case FieldType.Date:
                field.formFieldTypeLabel = 'Date';
                break;
            case FieldType.File:
                field.formFieldTypeLabel = 'File';
                break;
            case FieldType.MultiSelect:
                field.formFieldTypeLabel = 'MultiSelect';
                break;
            case FieldType.MultilineText:
                field.formFieldTypeLabel = 'MultilineText';
                break;
            case FieldType.Numeric:
                field.formFieldTypeLabel = 'Numeric';
                break;
            case FieldType.Radio:
                field.formFieldTypeLabel = 'Radio';
                break;
            case FieldType.Select:
                field.formFieldTypeLabel = 'Select';
                break;
            case FieldType.Text:
                field.formFieldTypeLabel = 'Text';
                break;
            default:
                field.formFieldTypeLabel = 'Unknown';
                break;
        }

        if (field.formFieldSourceData?.value === undefined) {
            return undefined;
        }

        const formFieldValueLocalConfiguration = field.formFieldConfiguration?.values?.[field.formFieldSourceData.value];
        const formFieldValueGlobalConfiguration = field.formFieldGlobalConfiguration?.values?.[field.formFieldSourceData.value];
        const formFieldValueTrialConfiguration = field.formFieldTrialConfiguration?.values?.[field.formFieldSourceData.value];



                
        let formFieldValueConfiguration: ISourceDataConfigurationFormFieldValue | undefined = undefined;
        let usingTrialConfiguration: boolean = false;
        let usingGlobalConfiguration: boolean = false;

        if (!!formFieldValueLocalConfiguration && !formFieldValueLocalConfiguration?.linked) {
            formFieldValueConfiguration = formFieldValueLocalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = false;
        }
        else if (!!formFieldValueGlobalConfiguration && !formFieldValueGlobalConfiguration.linked) {
            formFieldValueConfiguration = formFieldValueGlobalConfiguration;
            usingTrialConfiguration = false;
            usingGlobalConfiguration = true;
        }
        else {
            formFieldValueConfiguration = formFieldValueTrialConfiguration;
            usingTrialConfiguration = true;
            usingGlobalConfiguration = false;
        }

        const metadataValue = field.formFieldMetadata?.values?.find(x => x.id === field.formFieldSourceData?.value);



        field.formFieldValueKey = field.formFieldSourceData.value;

        field.formFieldValueConfiguration = formFieldValueConfiguration;
        field.formFieldValueLocalConfiguration = formFieldValueLocalConfiguration;
        field.formFieldValueGlobalConfiguration = formFieldValueGlobalConfiguration;
        field.formFieldValueTrialConfiguration = formFieldValueTrialConfiguration;
        field.formFieldValueMetadata = metadataValue;
        field.usingFormFieldValueGlobalConfiguration = usingGlobalConfiguration;
        field.usingFormFieldValueTrialConfiguration = usingTrialConfiguration;

        field.formFieldValue = await this.processFieldValue(field, metadataValue, field.formFieldSourceData?.value);

        await this.processFieldAudit(field);
    }
}

export default SourceDataFormProcessor;